import { Fab, LightColors, Tooltip } from "@thingsw/pitta-design-system";
import LayersIcon from "@material-ui/icons/Layers";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import FilterListIcon from "@material-ui/icons/FilterList";
import React, { forwardRef, useState } from "react";
import { Webviewer } from "../../contants/Breakpoints";

import { makeStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useMediaQuery, useTheme } from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    "& > button": {
      ...(theme.direction === "rtl"
        ? { marginLeft: theme.spacing(1) }
        : { marginRight: theme.spacing(1) }),
      "&:last-child": {
        ...(theme.direction === "rtl" ? { marginLeft: 0 } : { marginRight: 0 }),
      },
    },
  },
  roundRoot: {
    "& > button": {
      borderRadius: 12,
    },
  },
  // mantis - 9421,9416 전체화면일 때 툴팁 안나오는 이슈도 함께 수정 (Leehj)
  iconColor: {
    "&:focus": {
      color: LightColors.primary["1"],
    },
    "&:hover": {
      color: LightColors.primary["7"],
      ...(theme.direction === "rtl" ? { marginLeft: 0 } : { marginRight: 0 }),
    },
    "&:not(:hover)": {
      ...(theme.direction === "rtl" ? { marginLeft: 0 } : { marginRight: 0 }),
    },
    margin: "0!important",
  },
  active: {
    color: LightColors.primary["7"],
  },
  noHover: {
    "&:hover": {
      fill: LightColors.primary["1"],
      color: LightColors.primary["1"],
    },
    "&:active": {
      fill: LightColors.primary["7"],
      color: LightColors.primary["7"],
    },
  },
  noFocus: {
    "&:focus": {
      fill: LightColors.primary["1"],
      color: LightColors.primary["1"],
    },
  },
}));

interface TopRightControlProps {
  onFullscreen?: () => void;
  onLayers?: () => void;
  onDashcamPublic?: () => void;
  onEventLayers?: () => void;
  fullscreen?: boolean;
  activePublic?: boolean;
  round?: boolean;
  publicRef?: React.Ref<HTMLButtonElement>;
  eventRef?: React.Ref<HTMLButtonElement>;
}

export const TopRightControl = forwardRef<
  HTMLButtonElement,
  TopRightControlProps
>(
  (
    {
      onFullscreen,
      onLayers,
      onDashcamPublic,
      onEventLayers,
      fullscreen,
      publicRef,
      eventRef,
      round,
      activePublic,
    },
    ref
  ) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const theme = useTheme() as Theme;
    const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));

    // mantis - 7439, fullsize아이콘 툴팁 제어를 위하여 useState추가(Leehj)
    const [openFullSizeTooltip, setOpenFullSizeTooltip] = useState(false);

    return (
      <div className={clsx(classes.root, round && classes.roundRoot)}>
        {onDashcamPublic && (
          <Tooltip
            disableTouchListener={mobile}
            title={t("Public cameras") ?? "Public cameras"}
            PopperProps={{
              disablePortal: true,
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
          >
            <Fab
              onClick={onDashcamPublic}
              size="small"
              variant="rounded"
              className={clsx(classes.noFocus, { [classes.noHover]: mobile })}
              ref={publicRef}
            >
              <FilterListIcon />
              {/* mantis - 10283, Blackvue Live Camera 토글 버튼 ON->Off로 변경시 상단오른쪽에 파란점 생기도록 수정 (Leehj) */}
              {!activePublic && (
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: LightColors.primary["7"],
                    position: "absolute",
                    top: 3,
                    right: 3,
                  }}
                />
              )}
            </Fab>
          </Tooltip>
        )}

        {onLayers && (
          <Tooltip
            disableTouchListener={mobile}
            title={t("Map type") ?? "Map type"}
            PopperProps={{
              disablePortal: true,
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
          >
            <Fab
              size="small"
              variant="rounded"
              ref={ref}
              onClick={onLayers}
              className={clsx(classes.noFocus, { [classes.noHover]: mobile })}
            >
              <LayersIcon />
            </Fab>
          </Tooltip>
        )}
        {onEventLayers && (
          <Tooltip
            disableTouchListener={mobile}
            title={t("Event type") ?? "Event type"}
            PopperProps={{
              disablePortal: true,
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
          >
            <Fab
              size="small"
              variant="rounded"
              ref={eventRef}
              onClick={onEventLayers}
              className={clsx(classes.noFocus, { [classes.noHover]: mobile })}
            >
              <LayersIcon />
            </Fab>
          </Tooltip>
        )}

        {onFullscreen && (
          <Tooltip
            // mantis - 7439, fullsize아이콘 툴팁 제어를 위하여 툴팁 속성이용 (Leehj)
            PopperProps={{
              disablePortal: true,
              placement: "bottom-end",
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
            open={openFullSizeTooltip}
            onOpen={() => setOpenFullSizeTooltip(true)}
            onClose={() => setOpenFullSizeTooltip(false)}
            disableTouchListener={mobile}
            title={
              fullscreen ? `${t("Exit fullscreen")}` : `${t("Fullscreen")}`
            }
          >
            <Fab
              size="small"
              variant="rounded"
              // mantis - 7439, safari에서 지도 전체화면 종료 시에도 툴팁남아있는 이슈수정/ 아이콘 클릭하면 툴팁 close (Leehj)
              onClick={() => {
                onFullscreen();
                setOpenFullSizeTooltip(false);
              }}
              className={classes.iconColor}
            >
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </Fab>
          </Tooltip>
        )}
      </div>
    );
  }
);
